<template>
    <div class="session__list">
        <div class="session__list_main">
            <component-list
                ref="componentlist"
                :key="rerenderIndex"
                :component-grid="SessionListGrid"
                :component-table="SessionListTable"
                :component-calendar="SessionListCalendar"
                :component-table-name="'events'"
                :get-items-getter="'getEventOccurrences'"
                :get-items-dispatch="'getEventOccurrencesSummary'"
                :get-items-dispatch-data="eventId"
                :empty-state-title="'Sessions for this event will appear here. Go ahead and create one by clicking Create session.'"
                :filter-search-queryable="false"
                :filter-search-by-field="'nameDate'"
                :filter-select-active="false"
                :list-view-items="['table', 'grid']"
                :item-name="['session', 'sessions']"
                :table-preferences-active="true"
            >
                <template v-slot:beforemain>
                    <view-actions
                        ref="viewactions"
                        :raw="true"
                        :title="`${$t('ebs.sessions.list.title.sessionsTitle')} ${rawItems.length}`"
                        :size="'medium'"
                        :custom-hint-active="visibilitySubscriptions"
                        :custom-hint-text="customHintText"
                        :custom-hint-icon="'eye'"
                        :cancel-button-active="false"
                        :custom-button-active="eventHasStatusActive"
                        :custom-button-animate-loading="eventUserIsSubscribed"
                        :custom-button-animate-loading-event-name="'unsubscribe-event'"
                        :custom-button-text="eventUserIsSubscribed ? $t('ebs.sessions.list.buttons.unsubscribe') : $t('ebs.sessions.list.buttons.noSuitable')"
                        :custom-notify-active="!eventUserIsSubscribed"
                        :custom-notify-prevent-close="true"
                        :custom-notify-title="$t('ebs.sessions.list.title.expressInterest')"
                        :custom-notify-text="$t('ebs.sessions.list.title.youWillBeNotified')"
                        :custom-notify-cancel-text="$t('ebs.sessions.list.buttons.cancel')"
                        :custom-notify-submit-text="$t('ebs.sessions.list.buttons.expressInterest')"
                        :custom-notify-submit-animate-loading="true"
                        :custom-notify-submit-animate-loading-name="'subscribe-event'"
                        :submit-button-visibility="['event_coordinator']"
                        :submit-button-text="$t('ebs.sessions.list.buttons.createSession')"
                        :status-active="eventUserIsSubscribed"
                        :status-name="eventUserIsSubscribed ? $t('ebs.sessions.list.name.expressedInterest') : ''"
                        @emit-notify-custom-submit="subscribeEvent"
                        @emit-custom-hint="openPopPeople"
                        @emit-submit="createSession"
                        v-on="eventUserIsSubscribed ? { 'emit-custom': unsubscribeEvent } : {}"
                    />
                </template>
            </component-list>
        </div>
        <template v-if="visibilitySubscriptions">
            <pop-table
                ref="subscriptionstable"
                :item-name="['subscription', 'subscriptions']"
                :title-text="$t('ebs.sessions.list.title.eventSubscriptions')"
                :content-text="$t('ebs.sessions.list.text.listOfPeople')"
                :cancel-button-text="$t('ebs.sessions.list.buttons.close')"
                :submit-button-text="$t('ebs.sessions.list.buttons.remove')"
                :submit-button-animate-loading="false"
                :items="eventSubscriptionsTableMappedIdNameEmailUsername"
                :labels="[
                    {
                        key: 'name',
                        title: 'Name',
                        type: 'string',
                        sortable: true
                    },
                    {
                        key: 'email',
                        title: 'Email',
                        type: 'string',
                        sortable: true
                    }
                ]"
                :table-checkbox="true"
                :prevent-close="true"
                :eventbus-emit-name="'event-subscriptions-remove'"
                @emit-submit="openRemoveSubscriptionsConfirm"
            />
            <pop-notify
                ref="subscriptionsconfirm"
                :title-text="$t('ebs.sessions.list.notify.removeSubscriptions')"
                :content-text="$t('ebs.sessions.list.notify.selectedSubscriptions')"
                :cancel-button-text="$t('ebs.sessions.list.buttons.cancel')"
                :submit-button-danger="true"
                :submit-button-text="$t('ebs.sessions.list.buttons.confirm')"
                :prevent-close="true"
                @emit-submit="unsubscribeEventBulk"
            />
        </template>
    </div>
</template>

<script>
import ViewActions from '@/components/default/shared/ViewActions'
import SessionListGrid from '@/components/ebs/sessions/list/SessionListGrid'
import SessionListTable from '@/components/ebs/sessions/list/SessionListTable'
import SessionListCalendar from '@/components/ebs/sessions/list/SessionListCalendar'
import PopTable from '@/components/default/shared/PopTable'
import PopNotify from '@/components/default/shared/PopNotify'
import { User } from '@/User'
import ComponentList from '@/components/default/shared/ComponentList'

export default {
  name: 'session-list',
  components: {
    ComponentList,
    PopNotify,
    PopTable,
    ViewActions
  },
  data () {
    return {
      listView: 'table',
      user: new User(this.$store.getters.user),
      outputItems: [],
      rerenderIndex: 0,
      SessionListCalendar,
      SessionListTable,
      SessionListGrid
    }
  },
  computed: {
    eventId () {
      return this.$route.params.id
    },
    eventHasStatusActive: function () {
      return this.$store.getters.getEventHasStatusActive
    },
    eventUserIsSubscribed: function () {
      return this.$store.getters.getEventUserIsSubscribed
    },
    eventSubscriptions: function () {
      return this.$store.getters.getEventSubscriptions || []
    },
    eventSubscriptionsTableMappedIdNameEmailUsername () {
      return this.$store.getters.getEventSubscriptionsMappedIdNameEmailUsername
    },
    //
    subscriptionSuccess: function () {
      return this.$store.getters.getSubscriptionSuccess
    },
    //
    rawItems: function () {
      return this.$store.getters['getEventOccurrences']
    },
    //
    customHintText: function () {
      const subscriptions = this.eventSubscriptions
      let label
      if (subscriptions.length === 1) {
        label = `person has`
      } else {
        label = `people have`
      }
      return `${subscriptions.length} ${label} expressed interest in this event`
    },
    //
    visibilitySubscriptions: function () {
      return !!this.eventSubscriptions.length && this.user.isEventCoordinator()
    }
  },
  methods: {
    createSession () {
      this.$router.push({ name: 'ebs-session-add', params: { eventId: this.$route.params.id } })
    },
    getEvent: function () {
      const eventID = this.$route.params.id

      this.$eventBus.$emit('start-loading')
      this.$store.dispatch('getEventSummary', eventID).finally(() => {
        this.$eventBus.$emit('stop-loading')
        this.rerenderIndex++
      })
    },
    //
    openPopPeople () {
      this.$refs.subscriptionstable.openPop()
    },
    //
    subscribeEvent () {
      this.$store.dispatch('subscribeEvent').then(() => {
        if (this.$refs.viewactions.$refs.popnotifycustom) {
          this.$refs.viewactions.$refs.popnotifycustom.closeNotify()
        }
        this.$store.dispatch('clearSubscription')
        this.getEvent()
      }).finally(() => {
        this.$eventBus.$emit('stop-subscribe-event')
      })
    },
    unsubscribeEvent () {
      this.$store.dispatch('unsubscribeEvent').then(() => {
        this.getEvent()
      }).finally(() => {
        this.$eventBus.$emit('stop-unsubscribe-event')
      })
    },
    unsubscribeEventBulk () {
      const selected = this.$refs.subscriptionstable.selected
      const selectedOutput = selected.map(obj => obj.id)
      this.$store.dispatch('unsubscribeEventBulk', selectedOutput).then(() => {
        this.getEvent()
      }).finally(() => {
        this.closeRemoveSubscriptionsConfirm()
      })
    },
    //
    setFilterResult (data) {
      this.outputItems = data
    },
    switchListView (value) {
      this.listView = value
    },
    //
    openRemoveSubscriptionsConfirm () {
      this.$refs.subscriptionsconfirm.openNotify()
    },
    closeRemoveSubscriptionsConfirm () {
      if (this.$refs.subscriptionsconfirm) {
        this.$refs.subscriptionsconfirm.closeNotify()
      }
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/sessions/sessionlist";
</style>
